import type { DebugSettings, Settings, Store } from './contracts';
import { formatBuildStamp } from './ui/format';

/**
 * iOS camera mode: horizontal FOV of the iPhone main (wide) rear camera along the long
 * side of the video, in degrees. Constant (not measurable from getUserMedia); feeds
 * `viewportVFovDeg` the same way the sim debug slider (cameraVFovDeg) does.
 */
export const CAMERA_LONG_SIDE_FOV_DEG = 65;

export const DEFAULT_SETTINGS: Settings = {
  eyeHeightPreset: 'seated',
  eyeHeightM: 1.2,
  gpsThresholdM: 5,
  overlayOpacity: 1,
  viewDistanceM: 2000,
  declinationMode: 'auto',
  declinationDeg: 3.6,
  trackAlignment: false,
  showMinimap: true,
};

/** Mock position default: Luzerner Seebecken, in the inner zone off the Quai. */
export const DEFAULT_DEBUG: DebugSettings = {
  enabled: false,
  mockGps: false,
  mockLat: 47.0535,
  mockLon: 8.3155,
  mockAccuracyM: 3,
  mockSpeedKmh: 0,
  mockHeading: false,
  mockHeadingDeg: 150,
  showRaw: false,
  showHorizon: false,
  cameraVFovDeg: 63,
};

function createStore<T extends object>(key: string, defaults: T): Store<T> {
  let value: T = { ...defaults };
  try {
    const raw = localStorage.getItem(key);
    if (raw) value = { ...defaults, ...(JSON.parse(raw) as Partial<T>) };
  } catch {
    /* no storage (tests) or corrupt JSON: use defaults */
  }
  const subs = new Set<(v: T) => void>();
  return {
    get: () => value,
    update(patch) {
      value = { ...value, ...patch };
      try {
        localStorage.setItem(key, JSON.stringify(value));
      } catch {
        /* ignore */
      }
      subs.forEach((cb) => cb(value));
    },
    subscribe(cb) {
      subs.add(cb);
      return () => subs.delete(cb);
    },
  };
}

export const settingsStore: Store<Settings> = createStore('uferzone.settings.v1', DEFAULT_SETTINGS);
export const debugStore: Store<DebugSettings> = createStore('uferzone.debug.v1', DEFAULT_DEBUG);

export const APP_VERSION = `${__APP_VERSION__}+${__GIT_HASH__}`;
export const APP_BUILD_TIME = __BUILD_TIME__;
/** "0.1.0+f148740 · Build 23.09.2026 14:05" (build time in Europe/Zurich local time). */
export const APP_VERSION_LABEL = [APP_VERSION, formatBuildStamp(APP_BUILD_TIME)].filter(Boolean).join(' · ');
